import type { LayoutItem } from 'react-grid-layout';
import type { PermissionResource } from '@/lib/permissions';
import { isClinicModuleEnabled } from '@/lib/clinicModules';

export type DashboardWidgetId =
  | 'quick_actions'
  | 'today_appointments'
  | 'active_patients'
  | 'outstanding_invoices'
  | 'sms_balance'
  | 'todays_schedule'
  | 'month_summary'
  | 'operational_alerts'
  | 'clinical_alerts'
  | 'patient_messages'
  | 'my_tasks';

export interface WidgetDefinition {
  id: DashboardWidgetId;
  /** i18n key under `dashboard.widgets.*` */
  nameKey: string;
  /** Any of these `view` permissions is enough. Empty = always allowed. */
  permissionResources: PermissionResource[];
  moduleKey?: string;
  defaultW: number;
  defaultH: number;
  minW: number;
  minH: number;
  maxW?: number;
  maxH?: number;
}

export const WIDGET_CATALOG: Record<DashboardWidgetId, WidgetDefinition> = {
  quick_actions: {
    id: 'quick_actions',
    nameKey: 'quick_actions',
    permissionResources: [],
    defaultW: 12,
    defaultH: 2,
    minW: 4,
    minH: 1,
    maxH: 3,
  },
  today_appointments: {
    id: 'today_appointments',
    nameKey: 'today_appointments',
    permissionResources: ['appointments'],
    moduleKey: 'appointments',
    defaultW: 3,
    defaultH: 2,
    minW: 2,
    minH: 2,
    maxH: 3,
  },
  active_patients: {
    id: 'active_patients',
    nameKey: 'active_patients',
    permissionResources: ['patients'],
    moduleKey: 'patients',
    defaultW: 3,
    defaultH: 2,
    minW: 2,
    minH: 2,
    maxH: 3,
  },
  outstanding_invoices: {
    id: 'outstanding_invoices',
    nameKey: 'outstanding_invoices',
    permissionResources: ['invoices'],
    moduleKey: 'invoices',
    defaultW: 3,
    defaultH: 2,
    minW: 2,
    minH: 2,
    maxH: 3,
  },
  sms_balance: {
    id: 'sms_balance',
    nameKey: 'sms_balance',
    permissionResources: ['sms'],
    defaultW: 3,
    defaultH: 2,
    minW: 2,
    minH: 2,
    maxH: 3,
  },
  todays_schedule: {
    id: 'todays_schedule',
    nameKey: 'todays_schedule',
    permissionResources: ['appointments'],
    moduleKey: 'appointments',
    defaultW: 8,
    defaultH: 6,
    minW: 4,
    minH: 3,
  },
  month_summary: {
    id: 'month_summary',
    nameKey: 'month_summary',
    permissionResources: ['invoices', 'appointments'],
    defaultW: 4,
    defaultH: 4,
    minW: 3,
    minH: 3,
  },
  operational_alerts: {
    id: 'operational_alerts',
    nameKey: 'operational_alerts',
    permissionResources: ['invoices', 'sms'],
    defaultW: 4,
    defaultH: 3,
    minW: 3,
    minH: 2,
  },
  clinical_alerts: {
    id: 'clinical_alerts',
    nameKey: 'clinical_alerts',
    permissionResources: ['patients'],
    moduleKey: 'patients',
    defaultW: 4,
    defaultH: 3,
    minW: 3,
    minH: 2,
  },
  patient_messages: {
    id: 'patient_messages',
    nameKey: 'patient_messages',
    permissionResources: ['messages', 'communication'],
    moduleKey: 'patients',
    defaultW: 4,
    defaultH: 4,
    minW: 3,
    minH: 3,
  },
  my_tasks: {
    id: 'my_tasks',
    nameKey: 'my_tasks',
    permissionResources: ['tasks'],
    defaultW: 4,
    defaultH: 3,
    minW: 3,
    minH: 2,
  },
};

export const WIDGET_IDS = Object.keys(WIDGET_CATALOG) as DashboardWidgetId[];

/**
 * Create actions live in the page header, not the editable widget grid.
 * Kept in the catalog so stored layouts still parse; filtered out at render.
 */
export const PAGE_LEVEL_WIDGET_IDS = new Set<DashboardWidgetId>(['quick_actions']);

export function isPageLevelWidget(id: string): boolean {
  return PAGE_LEVEL_WIDGET_IDS.has(id as DashboardWidgetId);
}

export function filterPageLevelWidgets(layout: readonly LayoutItem[]): LayoutItem[] {
  return layout.filter((item) => !isPageLevelWidget(item.i));
}

/** Matches the previous static dashboard as closely as a 12-col grid allows. */
export const DEFAULT_DASHBOARD_LAYOUT: LayoutItem[] = [
  { i: 'today_appointments', x: 0, y: 0, w: 3, h: 2 },
  { i: 'active_patients', x: 3, y: 0, w: 3, h: 2 },
  { i: 'outstanding_invoices', x: 6, y: 0, w: 3, h: 2 },
  { i: 'sms_balance', x: 9, y: 0, w: 3, h: 2 },
  { i: 'todays_schedule', x: 0, y: 2, w: 8, h: 6 },
  { i: 'month_summary', x: 8, y: 2, w: 4, h: 4 },
  { i: 'operational_alerts', x: 8, y: 6, w: 4, h: 3 },
  { i: 'patient_messages', x: 8, y: 9, w: 4, h: 4 },
  { i: 'clinical_alerts', x: 8, y: 13, w: 4, h: 3 },
  { i: 'my_tasks', x: 8, y: 16, w: 4, h: 3 },
];

export function isDashboardWidgetId(value: string): value is DashboardWidgetId {
  return value in WIDGET_CATALOG;
}

export function isWidgetAllowed(
  def: WidgetDefinition,
  hasView: (resource: PermissionResource) => boolean,
  modules: Record<string, boolean> | null | undefined,
): boolean {
  if (def.moduleKey && !isClinicModuleEnabled(modules, def.moduleKey)) return false;
  if (def.permissionResources.length === 0) return true;
  return def.permissionResources.some((resource) => hasView(resource));
}

export function parseDashboardLayout(raw: unknown): LayoutItem[] {
  if (!Array.isArray(raw)) return [];
  const items: LayoutItem[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (typeof r.i !== 'string' || !isDashboardWidgetId(r.i)) continue;
    if (seen.has(r.i)) continue;
    if (typeof r.x !== 'number' || typeof r.y !== 'number') continue;
    if (typeof r.w !== 'number' || typeof r.h !== 'number') continue;
    seen.add(r.i);
    items.push({ i: r.i, x: r.x, y: r.y, w: r.w, h: r.h });
  }
  return items;
}

export function serializeDashboardLayout(layout: readonly LayoutItem[]): Array<{
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}> {
  return layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
}

export function withCatalogConstraints(layout: readonly LayoutItem[]): LayoutItem[] {
  return layout.map((item) => {
    if (!isDashboardWidgetId(item.i)) return item;
    const def = WIDGET_CATALOG[item.i];
    return {
      ...item,
      minW: def.minW,
      minH: def.minH,
      maxW: def.maxW,
      maxH: def.maxH,
    };
  });
}

export function filterLayoutByAccess(
  layout: readonly LayoutItem[],
  hasView: (resource: PermissionResource) => boolean,
  modules: Record<string, boolean> | null | undefined,
): LayoutItem[] {
  return layout.filter((item) => {
    if (!isDashboardWidgetId(item.i)) return false;
    return isWidgetAllowed(WIDGET_CATALOG[item.i], hasView, modules);
  });
}

/** Keep hidden (no-access) widgets in storage so they return if access is restored. */
export function mergeVisibleLayout(
  stored: readonly LayoutItem[],
  visible: readonly LayoutItem[],
  allowedIds: ReadonlySet<string>,
): LayoutItem[] {
  const hidden = stored.filter((item) => !allowedIds.has(item.i));
  return [...visible, ...hidden];
}

export function nextWidgetPlacement(
  layout: readonly LayoutItem[],
  def: WidgetDefinition,
): LayoutItem {
  const maxY = layout.reduce((m, item) => Math.max(m, item.y + item.h), 0);
  return { i: def.id, x: 0, y: maxY, w: def.defaultW, h: def.defaultH };
}
